import React, { useRef } from "react";
import { showMessageBox } from "@/components/blocks/message-box";
import { useTranslation } from "@/providers/language-provider";
import { Signal, signal } from "@preact/signals-react";
import { graphQLClient } from "@/lib/client-graphql";
import { useAuth } from "@/providers/auth-provider";
import { useSocket } from "@/providers/socket-provider";
import { useChats } from "@/providers/chat-provider";

export const FRIEND_EVENTS = [
  "FRIEND_REQUEST",
  "FRIEND_CANCEL",
  "FRIEND_ACCEPT",
  "FRIEND_REJECT",
  "FRIEND_UPDATE",
] as const;
export type FriendEvent = (typeof FRIEND_EVENTS)[number];

type FriendRequestFn = (request: any) => Promise<void>;

export const updateSentRequests = signal(async () => {});
export const updateReceivedRequests = signal(async () => {});

export const cancelFriendRequest = signal<FriendRequestFn | null>(null);
export const acceptFriendRequest = signal<FriendRequestFn | null>(null);
export const rejectFriendRequest = signal<FriendRequestFn | null>(null);

export const receivedRequests = signal<any[]>([]);
export const sentRequests = signal<any[]>([]);

export const isFetching = signal(false);

export const FriendManager = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isConnected, registerMessageHandler, unregisterMessageHandler } =
    useSocket();
  const { fetchChats } = useChats();
  const selfProcessedRequests = useRef<any[]>([]);

  React.useEffect(() => {
    if (isConnected) {
      getSentRequests();
      getReceivedRequests();
      updateSentRequests.value = getSentRequests;
      updateReceivedRequests.value = getReceivedRequests;
      cancelFriendRequest.value = handleCancelRequest;
      acceptFriendRequest.value = handleAcceptRequest;
      rejectFriendRequest.value = handleRejectRequest;
    }
  }, [isConnected]);

  const getSentRequests = async () => {
    isFetching.value = true;
    const data = await graphQLClient.sentFriendRequests(true);
    sentRequests.value = data;
    isFetching.value = false;
  };

  const getReceivedRequests = async () => {
    isFetching.value = true;
    const data = await graphQLClient.receivedFriendRequests(true);
    receivedRequests.value = data;
    isFetching.value = false;
  };

  const removeProcessedRequest = (request: any, target: Signal<any[]>) => {
    target.value = target.value.filter((r: any) => r._id !== request._id);
    selfProcessedRequests.current.push(request);
  };

  const handleCancelRequest = async (request: any) => {
    const choice = await showMessageBox({
      title: t("CancelFriend"),
      description: t("CancelFriendDesc"),
    });
    if (choice === "Yes") {
      isFetching.value = true;
      const data = await graphQLClient.cancelFriendRequest({
        friendshipId: request._id,
      });
      if (data?.status === "canceled") {
        console.log("[Friend] canceled friend request", request);
        removeProcessedRequest(request, sentRequests);
      } else {
        console.error("[Friend] request error", request);
      }
      isFetching.value = false;
    }
  };

  const handleAcceptRequest = async (request: any) => {
    const choice = await showMessageBox({
      title: t("AcceptFriend"),
      description: t("AcceptFriendDesc"),
    });
    if (choice === "Yes") {
      isFetching.value = true;
      const data = await graphQLClient.acceptFriendRequest({
        friendshipId: request._id,
      });
      if (data?.status === "accepted") {
        console.log("[Friend] accept friend request", request);
        removeProcessedRequest(request, receivedRequests);
      } else {
        console.error("[Friend] request error", request);
      }
      isFetching.value = false;
    }
  };

  const handleRejectRequest = async (request: any) => {
    const choice = await showMessageBox({
      title: t("CancelFriend"),
      description: t("CancelFriendDesc"),
    });
    if (choice === "Yes") {
      isFetching.value = true;
      const data = await graphQLClient.rejectFriendRequest({
        friendshipId: request._id,
      });
      if (data?.status === "rejected") {
        console.log("[Friend] reject friend request", request);
        removeProcessedRequest(request, receivedRequests);
      } else {
        console.error("[Friend] request error", request);
      }
      isFetching.value = false;
    }
  };

  const handleFriendUpdate = async (data: any) => {
    console.log(`[Friend] HandleFriendUpdate: `, data);
    const type = data.type;
    const friendship = data.data;
    // 셀프 처리한 요청인지 체크.
    const processedRequest = selfProcessedRequests.current.find(
      (r: any) => r._id === friendship._id,
    );
    if (processedRequest) {
      console.log("이미 처리한 요청", processedRequest);
      selfProcessedRequests.current = selfProcessedRequests.current.filter(
        (r: any) => r._id !== processedRequest._id,
      );
    } else {
      // 셀프 처리한 요청이 아닐 경우 친구 요청 업데이트.
      if (friendship.requester === user?._id) {
        await getSentRequests();
      } else {
        await getReceivedRequests();
      }
    }

    // 친구 승인시 친구 리스트 업데이트.
    if (type === "FRIEND_ACCEPT") {
      await fetchChats();
    }
    // 친구 정보 변경시 친구 리스트 업데이트.
    else if (type === "FRIEND_UPDATE") {
      await fetchChats();
    }
  };

  React.useEffect(() => {
    FRIEND_EVENTS.forEach((event) => {
      registerMessageHandler(event, handleFriendUpdate);
    });
    // Cleanup function
    return () => {
      FRIEND_EVENTS.forEach((event) => {
        unregisterMessageHandler(event);
      });
    };
  }, [handleFriendUpdate]);

  return null;
};
